import React, { useCallback, useState } from 'react';

import { Icon, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import { toaster } from 'components/toaster/toaster';
import { AddGiftCardInput } from 'experiments/checkout-v2/control/pages/cart/payment/add-gift-card-input';
import { useLoyaltyUpdateIdAndPayMutation } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getDesignToken } from 'styles/constants';
import logger from 'utils/logger';

import GiftCardImage from '../gift-card-image';
import { useCardNumber } from '../use-giftcard-number';

const GIFT_CARD_NUMBER_TRANSLATION_ID = 'giftCardNumber';

export const AddGiftCardModal: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    defaultPaymentMethodId,
    getPrepaidPaymentMethod,
    mergePrepaidCardBalances,
    setCheckoutPaymentMethodId,
  } = usePaymentContext();

  const { giftCardNumber, errors, isValidGiftCardNumber, handleCardNumberChange } = useCardNumber();
  const { loyaltyUser } = useLoyaltyUser();
  const [loyaltyQuickPayMutation] = useLoyaltyUpdateIdAndPayMutation();
  const router = useRouter();

  const handleCloseModal = () => {
    setCheckoutPaymentMethodId(defaultPaymentMethodId || CurrentSelectedMethodType.ADD_NEW_CARD);
    router.dismiss();
  };

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
    onCancel: handleCloseModal,
  });

  const addNewCard = useCallback(
    async (event: any) => {
      event.preventDefault();

      setIsLoading(true);

      if (getPrepaidPaymentMethod()) {
        return openErrorDialog({ message: t('giftCardExists') });
      }

      try {
        const { storedPaymentMethodId } = await mergePrepaidCardBalances({
          // Source pin is not required, BK crown cards don't have it
          sourceCardNumber: giftCardNumber,
        });
        loyaltyQuickPayMutation({
          variables: {
            idAndPayInput: { storedPaymentMethodId, enabled: true },
            loyaltyId: loyaltyUser?.id,
          },
        });

        setCheckoutPaymentMethodId(storedPaymentMethodId);
        toaster.giftCardAddedToast();
        router.dismiss();
      } catch (error) {
        openErrorDialog({ message: t('giftCardAddingError'), error });
        logger.error({ error, message: 'Error adding gift card' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      getPrepaidPaymentMethod,
      openErrorDialog,
      mergePrepaidCardBalances,
      giftCardNumber,
      loyaltyQuickPayMutation,
      loyaltyUser?.id,
      setCheckoutPaymentMethodId,
      router,
    ]
  );

  const submitButtonText = `${t('save')} & ${t('continue')}`;

  return (
    <CardModal
      analyticsEvent="Add gift card"
      testID="add-giftcard-form"
      onDismiss={handleCloseModal}
      standardPadding
      headerText={t('addNewPaymentMethodGiftCard')}
      footer={
        <ActionButton
          fullWidth
          testID="save-and-continue"
          isLoading={isLoading}
          disabled={!isValidGiftCardNumber}
          onPress={addNewCard}
        >
          {submitButtonText}
        </ActionButton>
      }
    >
      <GiftCardImage />
      <AddGiftCardInput
        onChange={handleCardNumberChange}
        errorMessage={errors?.giftCardNumber}
        label={t(GIFT_CARD_NUMBER_TRANSLATION_ID as TranslationKey)}
        testID="add-giftcard-input"
      />
      <ErrorDialog />
    </CardModal>
  );
};

export const Info = Text.withConfig({
  marginY: '$0',
  marginRight: '$0',
  marginLeft: '$4',
  variant: 'copyOne',
});

export const SuccessIcon = Icon.withConfig({
  variant: 'bkCrown',
  size: '$12',
  alignSelf: 'center',
  color: getDesignToken('crown-color'),
});
